import { Box, Skeleton, TableBody, TableHead } from '@mui/material'

import ScrollableTableContainer from 'components/Table/ScrollableTableContainer'
import {
  Table as EnhancedTable,
  TableCell as EnhancedTableCell,
  TableHeadCell,
  TableNameWrapper,
  TableNickname,
  TableRow as EnhancedTableRow
} from 'components/Table/Table.styles'

import { theme } from 'styles/theme'

type BalanceTableSkeletonProps = {
  rows?: number
}

const HEADERS = [
  'Tipo',
  'Descrição',
  'Data de Vencimento',
  'Data Pagamento',
  'Valor Pago',
  'Tarifa bancária',
  'Valor Líquido',
  'Método de Pagamento',
  ''
]

// Larguras variadas por linha pra o skeleton não virar um bloco uniforme —
// cada índice é reaproveitado ciclicamente conforme a quantidade de linhas.
const DESCRIPTION_WIDTHS = [210, 180, 240, 160, 200, 230, 170, 190]
const NICKNAME_WIDTHS = [90, 70, 110, 0, 85, 60, 100, 75]
const PAYMENT_WIDTHS = [140, 110, 150, 95, 130, 120, 145, 105]

// Altura da linha de texto real (14px * 1.5) — manter a barra nessa altura faz
// a linha do skeleton ocupar o mesmo espaço que a linha com dado.
const TEXT_LINE_HEIGHT = 21
const NICKNAME_LINE_HEIGHT = 18
// ButtonRounded size="lg" — é ele quem estica a linha na coluna de ações.
const ACTION_BUTTON_SIZE = 42

// `muted` deixa a barra mais clara — usado no apelido, que no dado real é o
// texto secundário cinza ao lado da descrição.
const bar = (
  width: number | string,
  { lineHeight = TEXT_LINE_HEIGHT, barHeight = 14, muted = false } = {}
) => (
  <Box display="flex" alignItems="center" height={lineHeight}>
    <Skeleton
      variant="text"
      width={width}
      height={barHeight}
      animation="wave"
      sx={{
        bgcolor: muted
          ? theme.palette.neutral[800]
          : theme.palette.neutral[700],
        transform: 'none',
        borderRadius: '4px'
      }}
    />
  </Box>
)

export const BalanceTableSkeleton = ({
  rows = 8
}: BalanceTableSkeletonProps) => (
  <ScrollableTableContainer>
    <EnhancedTable>
      <TableHead>
        <EnhancedTableRow>
          {HEADERS.map((label, index) => (
            <TableHeadCell key={`balance-skeleton-head-${index}`}>
              {label}
            </TableHeadCell>
          ))}
        </EnhancedTableRow>
      </TableHead>

      <TableBody>
        {Array.from({ length: rows }).map((_, rowIndex) => {
          const descriptionWidth =
            DESCRIPTION_WIDTHS[rowIndex % DESCRIPTION_WIDTHS.length]
          const nicknameWidth =
            NICKNAME_WIDTHS[rowIndex % NICKNAME_WIDTHS.length]
          const paymentWidth = PAYMENT_WIDTHS[rowIndex % PAYMENT_WIDTHS.length]

          return (
            <EnhancedTableRow
              key={`balance-skeleton-row-${rowIndex}`}
              clickable={false}
            >
              <EnhancedTableCell clickable={false}>
                <Skeleton
                  variant="circular"
                  width={20}
                  height={20}
                  animation="wave"
                  sx={{ bgcolor: theme.palette.neutral[700] }}
                />
              </EnhancedTableCell>

              {/* Mesma marcação da célula real (o <p> com margem padrão é o que
                  define a altura da linha na tabela com dado). */}
              <EnhancedTableCell clickable={false}>
                <TableNameWrapper>
                  <p>{bar(descriptionWidth)}</p>
                  {!!nicknameWidth && (
                    <TableNickname>
                      {bar(nicknameWidth, {
                        lineHeight: NICKNAME_LINE_HEIGHT,
                        barHeight: 12,
                        muted: true
                      })}
                    </TableNickname>
                  )}
                </TableNameWrapper>
              </EnhancedTableCell>

              <EnhancedTableCell clickable={false}>{bar(84)}</EnhancedTableCell>
              <EnhancedTableCell clickable={false}>{bar(84)}</EnhancedTableCell>
              <EnhancedTableCell clickable={false}>{bar(62)}</EnhancedTableCell>
              <EnhancedTableCell clickable={false}>{bar(54)}</EnhancedTableCell>
              <EnhancedTableCell clickable={false}>{bar(62)}</EnhancedTableCell>
              <EnhancedTableCell clickable={false}>
                {bar(paymentWidth)}
              </EnhancedTableCell>

              <EnhancedTableCell clickable={false}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  height={ACTION_BUTTON_SIZE}
                  width={ACTION_BUTTON_SIZE}
                >
                  <Skeleton
                    variant="circular"
                    width={24}
                    height={24}
                    animation="wave"
                    sx={{ bgcolor: theme.palette.neutral[700] }}
                  />
                </Box>
              </EnhancedTableCell>
            </EnhancedTableRow>
          )
        })}
      </TableBody>
    </EnhancedTable>
  </ScrollableTableContainer>
)

export default BalanceTableSkeleton
